import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import Sidebar from '../components/Sidebar';
import ArtefactTabs, { type ArtefactTab } from '../components/ArtefactTabs';
import type { ArtefactMeta } from '../components/ArtefactDetailContent';
import { getArtefactDisplayName } from '../utils/artefactDisplayName';
import { useResizablePanel } from '../hooks/useResizablePanel';
import { Phase25RecoveryNotice, buildPhase25RecoveryNotice, type Phase25RecoveryNoticeModel, type Phase25RecoveryResponse } from '../components/Phase25RecoveryNotice';

const PANEL_STORAGE_KEY = 'blueprint_artefacts_panel_width';
const PANEL_DEFAULT_WIDTH = 480;
const PANEL_MIN_WIDTH = 280;
const PANEL_MAX_WIDTH_FACTOR = 0.6;

interface Artefact {
  id: string;
  title: string;
  type: string;
  status: string;
  source?: string;
  // Top-level import provenance. Non-null means the artefact was originally
  // uploaded by a user (filename stored here). Stable across subsequent
  // version edits, unlike the version-level `source` field.
  importedFrom?: string | null;
  phase?: string | null;
  currentVersion: string;
  projectId: string;
  projectName: string | null;
  projectPhase: string | null;
  updatedAt: string;
}

interface AnalyseResponse {
  sessionId: string;
  projectId: string;
}

interface BARResponse {
  reportId: string;
  readinessScore: number;
  summary: string;
  artefactCount: number;
  riskCount: number;
  phaseCount: number;
  recommendedEntryPoint: string;
}

// ── Display maps ────────────────────────────────────────────────────────────


const TYPE_ICONS: Record<string, string> = {
  prd: '📄', sad: '📐', adr: '📋', user_stories: '📝', analysis_report: '🔍',
  user_journey_map: '🗺️',
  screen_inventory: '🖥️',
  screen_spec: '🧩',
  component_catalogue: '🧱',
  design_tokens: '🎨',
  sprint_plan: '📊', sprint_backlog: '📦', test_plan: '🧪',
  deployment_runbook: '🚀', retro_report: '🔄', erd: '🗄️',
  handoff_package: '📨', amendment_request: '📝',
  implementation_prompt: '⚙️', dev_response: '💻',
  sprint_contract: '🤝', code_review_report: '🔎', qa_report: '🐞',
  change_order: '🔧', calibration_report: '🎯', gate_report: '🚦',
  brownfield_assessment: '🏗️',
  codebase_summary: '💻',
};

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  draft:        { bg: 'rgba(245,158,11,0.12)', color: 'var(--warning)' },
  approved:     { bg: 'rgba(16,185,129,0.12)', color: 'var(--success)' },
  review:       { bg: 'rgba(245,158,11,0.12)', color: 'var(--warning)' },
  active:       { bg: 'rgba(16,185,129,0.12)', color: 'var(--success)' },
  handed_off:   { bg: 'rgba(124,58,237,0.10)', color: 'var(--primary)' },
  superseded:   { bg: 'rgba(107,114,128,0.10)', color: 'var(--text-secondary)' },
  imported:     { bg: 'rgba(59,130,246,0.12)',  color: '#3B82F6' },
  needs_review: { bg: 'rgba(245,158,11,0.12)', color: 'var(--warning)' },
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', approved: 'Approved', review: 'In Review',
  handed_off: 'Handed Off', active: 'Active', superseded: 'Superseded',
  imported: 'Imported', needs_review: 'Needs Review',
};

const PHASE_MAP: Record<string, { phase: string; order: number }> = {
  prd:                  { phase: 'Phase 1 — Requirements',    order: 1 },
  user_stories:         { phase: 'Phase 1 — Requirements',    order: 1 },
  analysis_report:      { phase: 'Assessment Reports',        order: -1 },
  brownfield_assessment:{ phase: 'Assessment Reports',        order: -1 },
  codebase_summary:     { phase: 'Phase 2 — Architecture',    order: 2 },
  sad:                  { phase: 'Phase 2 — Architecture',    order: 2 },
  adr:                  { phase: 'Phase 2 — Architecture',    order: 2 },
  user_journey_map:     { phase: 'Phase 2.5 — UX Design',     order: 2.5 },
  screen_inventory:     { phase: 'Phase 2.5 — UX Design',     order: 2.5 },
  screen_spec:          { phase: 'Phase 2.5 — UX Design',     order: 2.5 },
  component_catalogue:  { phase: 'Phase 2.5 — UX Design',     order: 2.5 },
  design_tokens:        { phase: 'Phase 2.5 — UX Design',     order: 2.5 },
  sprint_plan:          { phase: 'Phase 3 — Sprint Planning', order: 3 },
  sprint_backlog:       { phase: 'Phase 3 — Sprint Planning', order: 3 },
  implementation_prompt:{ phase: 'Phase 4 — Development',     order: 4 },
  dev_response:         { phase: 'Phase 4 — Development',     order: 4 },
  sprint_contract:      { phase: 'Phase 4 — Development',     order: 4 },
  calibration_report:   { phase: 'Phase 4 — Development',     order: 4 },
  code_review_report:   { phase: 'Phase 5 — Code Review',     order: 5 },
  test_plan:            { phase: 'Phase 6 — Testing',         order: 6 },
  qa_report:            { phase: 'Phase 6 — Testing',         order: 6 },
  deployment_runbook:   { phase: 'Phase 7 — Deployment',      order: 7 },
  retro_report:         { phase: 'Phase 8 — Retrospective',   order: 8 },
  handoff_package:      { phase: 'Handoff Packages',          order: 9 },
  change_order:         { phase: 'Change Orders',             order: 10 },
  amendment_request:    { phase: 'Amendment Requests',        order: 11 },
  gate_report:          { phase: 'Phase Gate Reports',        order: 12 },
};

// Within a phase group, default sort is `updatedAt DESC`. That puts
// recently-touched (or backfilled) artefacts at the top, which is the
// wrong reading for Phase 2.5 — the canonical step order is Journey
// Map → Screen Inventory → Screen Spec → Component Catalogue → Design
// Tokens regardless of when each row was written. After the Phase 2.5
// recovery on Brazil Payroll Calculator (PR #696), the three recovered
// types had the newest `updatedAt` and elbowed the older Journey Map +
// Design Tokens to the bottom of the group.
//
// TYPE_SUB_ORDER is a per-type tiebreaker applied BEFORE the
// updatedAt-DESC fallback. Types without an entry fall to a high
// default and keep the updatedAt ordering, so adding sub-orders for
// other phases later is just a dictionary entry — no other call sites
// to update.
const TYPE_SUB_ORDER: Record<string, number> = {
  user_journey_map:    1,
  screen_inventory:    2,
  screen_spec:         3,
  component_catalogue: 4,
  design_tokens:       5,
};

const PHASE_LABEL: Record<string, string> = {
  phase_0_bootstrap:      'Bootstrap',
  phase_1_requirements:   'Requirements',
  phase_2_architecture:   'Architecture',
  phase_2_5_ux_design:    'UX Design',
  phase_3_sprint_planning:'Sprint Planning',
  phase_4_development:    'Development',
  phase_5_code_review:    'Code Review',
  phase_6_testing:        'Testing',
  phase_7_deployment:     'Deployment',
  phase_8_retrospective:  'Retrospective',
};

/** Map from artefact version phase_id to the same display key/order used by PHASE_MAP */
const PHASE_ID_TO_GROUP: Record<string, { phase: string; order: number }> = {
  phase_0_bootstrap:       { phase: 'Phase 0 — Bootstrap',       order: 0 },
  phase_1_requirements:    { phase: 'Phase 1 — Requirements',    order: 1 },
  phase_2_architecture:    { phase: 'Phase 2 — Architecture',    order: 2 },
  phase_2_5_ux_design:     { phase: 'Phase 2.5 — UX Design',     order: 2.5 },
  phase_3_sprint_planning: { phase: 'Phase 3 — Sprint Planning', order: 3 },
  phase_4_development:     { phase: 'Phase 4 — Development',     order: 4 },
  phase_5_code_review:     { phase: 'Phase 5 — Code Review',     order: 5 },
  phase_6_testing:         { phase: 'Phase 6 — Testing',         order: 6 },
  phase_7_deployment:      { phase: 'Phase 7 — Deployment',      order: 7 },
  phase_8_retrospective:   { phase: 'Phase 8 — Retrospective',   order: 8 },
};

function phaseDisplayName(phase: string | null): string {
  if (!phase) return 'Unknown';
  // Sprint 11 UX.A1: /^phase_\d+_/ would strip only the first digit segment
  // (`phase_2_5_ux_design` → "5 ux design"). Greedy [\d_]+ handles fractional
  // phases when the explicit map below misses a future entry.
  return PHASE_LABEL[phase] ?? phase.replace(/^phase_[\d_]+_/, '').replace(/_/g, ' ');
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// ── Persistence ─────────────────────────────────────────────────────────────

const STORAGE_KEY = 'blueprint:artefacts:expand-state';

function loadExpandState(): Record<string, Set<string>> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, string[]>;
    const result: Record<string, Set<string>> = {};
    for (const [k, v] of Object.entries(parsed)) result[k] = new Set(v);
    return result;
  } catch { return {}; }
}

function saveExpandState(state: Record<string, Set<string>>) {
  const serializable: Record<string, string[]> = {};
  for (const [k, v] of Object.entries(state)) serializable[k] = [...v];
  localStorage.setItem(STORAGE_KEY, JSON.stringify(serializable));
}

// ── Component ───────────────────────────────────────────────────────────────

const MAX_TABS = 5;

interface ProjectGroup {
  projectId: string;
  name: string;
  phase: string | null;
  lastActivity: string;
  count: number;
  phases: { phase: string; order: number; items: Artefact[] }[];
}

export default function ArtefactsPage() {
  const [artefacts, setArtefacts] = useState<Artefact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Tab state
  const [openTabs, setOpenTabs] = useState<ArtefactTab[]>([]);
  const [activeTabId, setActiveTabId] = useState('');
  const tabsInitialized = useRef(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Evidence link counts per artefact (sourceId → count)
  const [evidenceCounts, setEvidenceCounts] = useState<Map<string, number>>(new Map());

  // Analyse state
  const [analysing, setAnalysing] = useState(false);
  const [analysingProjectId, setAnalysingProjectId] = useState<string | null>(null);

  // BAR generation state
  const [generatingBAR, setGeneratingBAR] = useState(false);
  const [barProjectId, setBarProjectId] = useState<string | null>(null);

  // Phase 2.5 recovery state — used by the per-project "Recover UX
  // artefacts" button that backfills any screen_inventory / screen_spec
  // / component_catalogue rows the live agent loop missed. Auto-clears
  // the notice after a few seconds so it doesn't linger.
  const [recoveringProjectId, setRecoveringProjectId] = useState<string | null>(null);
  // #3708 — structured, kept until dismissed (no auto-clear), warning when anything was not extractable.
  const [recoveryNotice, setRecoveryNotice] = useState<Phase25RecoveryNoticeModel | null>(null);
  const recoveryNoticeRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // The notice renders above the panels; bring it into view where the user clicked.
    if (recoveryNotice) recoveryNoticeRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [recoveryNotice]);

  // Filter state — honour ?projectId= from URL (e.g. gate action links)
  const [filterProject, setFilterProject] = useState<string>(searchParams.get('projectId') ?? 'all');
  const [search, setSearch] = useState('');

  // Expand state: expandedProjects tracks open project IDs, expandedPhases tracks open phases per project
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
  const [expandedPhases, setExpandedPhases] = useState<Record<string, Set<string>>>(() => loadExpandState());

  // Resizable left panel — width persisted to localStorage; drag handle + double-click reset.
  // panelRef lets the hook translate cursor clientX into a width relative to
  // the panel's own left edge (the page has a 240px fixed sidebar in front).
  const leftPanelRef = useRef<HTMLDivElement>(null);
  const {
    width: panelWidth,
    minWidth: panelMin,
    maxWidth: panelMax,
    isDragging: panelDragging,
    handleProps: panelHandleProps,
  } = useResizablePanel({
    storageKey: PANEL_STORAGE_KEY,
    defaultWidth: PANEL_DEFAULT_WIDTH,
    minWidth: PANEL_MIN_WIDTH,
    maxWidthFactor: PANEL_MAX_WIDTH_FACTOR,
    panelRef: leftPanelRef,
  });

  // Save expand state to localStorage on change
  useEffect(() => {
    saveExpandState(expandedPhases);
  }, [expandedPhases]);

  // Fetch artefacts
  useEffect(() => {
    apiFetch<Artefact[]>('/artefacts')
      .then(data => {
        setArtefacts(data);
        // Auto-expand: prefer projectId from URL, else most recently active
        if (data.length > 0) {
          const urlProjectId = searchParams.get('projectId');
          const targetId = urlProjectId && data.some(a => a.projectId === urlProjectId)
            ? urlProjectId
            : [...data].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0].projectId;
          setExpandedProjects(new Set([targetId]));
        }
        if (!tabsInitialized.current) {
          tabsInitialized.current = true;
          const tabIds = searchParams.get('tabs')?.split(',').filter(Boolean) ?? [];
          const activeId = searchParams.get('active') ?? '';
          if (tabIds.length > 0) {
            const initialTabs: ArtefactTab[] = tabIds.map(id => {
              const a = data.find(x => x.id === id);
              return { artefactId: id, title: a?.title ?? '…', type: a?.type ?? '', status: a?.status ?? '' };
            });
            setOpenTabs(initialTabs);
            setActiveTabId(activeId || tabIds[tabIds.length - 1]);
          }
        }
      })
      .catch(err => setError(err instanceof Error ? err.message : 'Failed to load artefacts'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch when the tab regains focus so backend changes that landed while
  // this tab was hidden — most commonly Phase 2.5 artefact recovery from a
  // chat session in another tab, or the diagnostic recovery endpoint — are
  // visible without a hard-refresh. Only the list is re-set; auto-expand and
  // tab restore stay one-shot from the initial mount above.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      apiFetch<Artefact[]>('/artefacts').then(setArtefacts).catch(() => {});
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  // Fetch evidence link counts once artefacts are loaded
  useEffect(() => {
    if (artefacts.length === 0) return;
    const projectIds = [...new Set(artefacts.map(a => a.projectId))];
    Promise.all(
      projectIds.map(pid =>
        apiFetch<{ sourceId: string }[]>(`/projects/${pid}/evidence`).catch(() => [] as { sourceId: string }[])
      )
    ).then(results => {
      const counts = new Map<string, number>();
      for (const rows of results) {
        for (const row of rows) {
          counts.set(row.sourceId, (counts.get(row.sourceId) ?? 0) + 1);
        }
      }
      setEvidenceCounts(counts);
    });
  }, [artefacts]);

  // Sync tabs from URL
  useEffect(() => {
    if (!tabsInitialized.current) return;
    const tabIds = [...new Set(searchParams.get('tabs')?.split(',').filter(Boolean) ?? [])].slice(0, MAX_TABS);
    const activeId = searchParams.get('active') ?? '';
    if (tabIds.length === 0) return;
    const newTabs: ArtefactTab[] = tabIds.map(id => {
      const a = artefacts.find(x => x.id === id);
      return { artefactId: id, title: a?.title ?? '…', type: a?.type ?? '', status: a?.status ?? '' };
    });
    queueMicrotask(() => {
      setOpenTabs(newTabs);
      setActiveTabId(tabIds.includes(activeId) ? activeId : tabIds[tabIds.length - 1]);
    });
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tab management
  const openTab = useCallback((a: Artefact) => {
    setOpenTabs(prev => {
      if (prev.find(t => t.artefactId === a.id)) { setActiveTabId(a.id); return prev; }
      // Tab header must match what the list row showed — use the canonical
      // display name so switching between list and tab bar isn't disorienting.
      const tabTitle = getArtefactDisplayName(
        a.importedFrom ? 'imported' : a.type,
        a.title,
      );
      const newTab: ArtefactTab = { artefactId: a.id, title: tabTitle, type: a.type, status: a.status };
      if (prev.length < MAX_TABS) { setActiveTabId(a.id); return [...prev, newTab]; }
      const inactiveIdx = prev.findIndex(t => t.artefactId !== activeTabId);
      const updated = [...prev];
      updated.splice(inactiveIdx === -1 ? 0 : inactiveIdx, 1);
      updated.push(newTab);
      setActiveTabId(a.id);
      return updated;
    });
  }, [activeTabId]);

  const closeTab = useCallback((artefactId: string) => {
    setOpenTabs(prev => {
      const idx = prev.findIndex(t => t.artefactId === artefactId);
      const next = prev.filter(t => t.artefactId !== artefactId);
      if (artefactId === activeTabId && next.length > 0) {
        setActiveTabId(next[Math.min(idx, next.length - 1)].artefactId);
      } else if (next.length === 0) { setActiveTabId(''); }
      return next;
    });
  }, [activeTabId]);

  const handleMetadataLoaded = useCallback((artefactId: string, meta: ArtefactMeta) => {
    setOpenTabs(prev => prev.map(t => t.artefactId === artefactId ? { ...t, ...meta } : t));
    setArtefacts(prev => prev.map(a => a.id === artefactId ? { ...a, title: meta.title, type: meta.type, status: meta.status } : a));
  }, []);

  // Abort guards for async operations
  const analyseAbort = useRef(false);
  const barAbort = useRef(false);
  useEffect(() => () => { analyseAbort.current = true; barAbort.current = true; }, []);

  const handleAnalyse = useCallback(async (projectId: string) => {
    const imported = artefacts.filter(a => a.projectId === projectId && a.source === 'imported');
    if (imported.length === 0) return;
    analyseAbort.current = false;
    setAnalysing(true);
    setAnalysingProjectId(projectId);
    setError('');
    try {
      // Create PM Agent session with gap analysis context
      const { sessionId } = await apiFetch<AnalyseResponse>(
        `/projects/${projectId}/analyse`,
        { method: 'POST', body: JSON.stringify({ artefactIds: imported.map(a => a.id) }) },
      );
      if (analyseAbort.current) return;

      // Navigate to chat — ChatPage will auto-send the first message
      navigate(`/projects/${projectId}/chat?sessionId=${sessionId}&autoAnalyse=true`);
    } catch (e: unknown) {
      if (!analyseAbort.current) setError(e instanceof Error ? e.message : 'Analysis failed');
    } finally {
      if (!analyseAbort.current) {
        setAnalysing(false);
        setAnalysingProjectId(null);
      }
    }
  }, [artefacts, navigate]);

  // Phase 2.5 artefact recovery — re-scans UX-agent chat sessions for the
  // currently-expanded project and backfills any screen_inventory /
  // screen_spec / component_catalogue rows that the live-agent regex
  // missed. Wraps POST /artefacts/projects/:projectId/recover-phase-2-5.
  // Designed for old projects that completed Phase 2.5 before the
  // post-stage-7 retro-save shipped (PR #694) — newer projects auto-heal
  // and won't usually need this button.
  const handleRecoverPhase2_5 = useCallback(async (projectId: string) => {
    setRecoveringProjectId(projectId);
    setRecoveryNotice(null);
    setError('');
    try {
      const result = await apiFetch<Phase25RecoveryResponse>(
        `/artefacts/projects/${projectId}/recover-phase-2-5`,
        { method: 'POST' },
      );
      setRecoveryNotice(buildPhase25RecoveryNotice(result));

      // Refresh the list so any newly-recovered rows surface immediately.
      // Failure here is silent — the recovery already succeeded; the list
      // will pick up changes on the next visibility cycle.
      const refreshed = await apiFetch<Artefact[]>('/artefacts').catch(() => null);
      if (refreshed) setArtefacts(refreshed);
    } catch (e: unknown) {
      setError(e instanceof Error ? `Recovery failed: ${e.message}` : 'Recovery failed');
    } finally {
      setRecoveringProjectId(null);
      // #3708 — no auto-clear: the notice stays until dismissed or the next recovery.
    }
  }, []);

  // Generate Brownfield Assessment Report
  const handleGenerateBAR = useCallback(async (projectId: string) => {
    barAbort.current = false;
    setGeneratingBAR(true);
    setBarProjectId(projectId);
    setError('');
    try {
      const result = await apiFetch<BARResponse>(
        `/projects/${projectId}/artefacts/brownfield-assessment`,
        { method: 'POST' },
      );
      if (barAbort.current) return;
      const refreshed = await apiFetch<Artefact[]>('/artefacts');
      if (barAbort.current) return;
      setArtefacts(refreshed);
      const report = refreshed.find(a => a.id === result.reportId);
      if (report) openTab(report);
    } catch (e: unknown) {
      if (!barAbort.current) setError(e instanceof Error ? e.message : 'Assessment generation failed');
    } finally {
      if (!barAbort.current) {
        setGeneratingBAR(false);
        setBarProjectId(null);
      }
    }
  }, [openTab]);

  // Toggle handlers
  const toggleProject = (projectId: string) => {
    setExpandedProjects(prev => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId); else next.add(projectId);
      return next;
    });
  };

  const togglePhase = (projectId: string, phase: string) => {
    setExpandedPhases(prev => {
      const current = prev[projectId] ?? new Set<string>();
      const next = new Set(current);
      if (next.has(phase)) next.delete(phase); else next.add(phase);
      return { ...prev, [projectId]: next };
    });
  };

  // Build project groups
  const projectGroups: ProjectGroup[] = useMemo(() => {
    const searchLower = search.toLowerCase();
    const filtered = artefacts.filter(a => {
      if (filterProject !== 'all' && a.projectId !== filterProject) return false;
      if (searchLower && !a.title.toLowerCase().includes(searchLower)) return false;
      return true;
    });

    const groupMap: Record<string, { name: string; phase: string | null; items: Artefact[] }> = {};
    for (const a of filtered) {
      if (!groupMap[a.projectId]) groupMap[a.projectId] = { name: a.projectName ?? 'Unknown Project', phase: a.projectPhase, items: [] };
      groupMap[a.projectId].items.push(a);
    }

    return Object.entries(groupMap).map(([projectId, g]) => {
      const phaseMap: Record<string, { order: number; items: Artefact[] }> = {};
      for (const a of g.items) {
        // Prefer the artefact's own phase (set during import classification),
        // fall back to type-based lookup, then "Other"
        const pm = (a.phase ? PHASE_ID_TO_GROUP[a.phase] : undefined)
          ?? PHASE_MAP[a.type]
          ?? { phase: 'Other', order: 99 };
        if (!phaseMap[pm.phase]) phaseMap[pm.phase] = { order: pm.order, items: [] };
        phaseMap[pm.phase].items.push(a);
      }
      const SUB_ORDER_DEFAULT = 1000;
      const phases = Object.entries(phaseMap)
        .map(([phase, v]) => ({
          phase,
          order: v.order,
          // Canonical step order within the phase, tiebroken by updatedAt
          // DESC. See TYPE_SUB_ORDER for the rationale.
          items: [...v.items].sort((x, y) => {
            const sx = TYPE_SUB_ORDER[x.type] ?? SUB_ORDER_DEFAULT;
            const sy = TYPE_SUB_ORDER[y.type] ?? SUB_ORDER_DEFAULT;
            if (sx !== sy) return sx - sy;
            return new Date(y.updatedAt).getTime() - new Date(x.updatedAt).getTime();
          }),
        }))
        .sort((a, b) => a.order - b.order);

      const lastActivity = g.items.reduce((latest, a) =>
        new Date(a.updatedAt) > new Date(latest) ? a.updatedAt : latest, g.items[0].updatedAt);

      return { projectId, name: g.name, phase: g.phase, lastActivity, count: g.items.length, phases };
    }).sort((a, b) => new Date(b.lastActivity).getTime() - new Date(a.lastActivity).getTime());
  }, [artefacts, filterProject, search]);

  // Projects that have imported artefacts (for the Analyse button)
  const projectsWithImported = useMemo(() => {
    const result = new Map<string, number>();
    for (const a of artefacts) {
      if (a.source === 'imported') {
        result.set(a.projectId, (result.get(a.projectId) ?? 0) + 1);
      }
    }
    return result;
  }, [artefacts]);

  // Projects that already have a brownfield_assessment artefact
  const projectsWithBAR = useMemo(() => {
    const result = new Map<string, Artefact>();
    for (const a of artefacts) {
      if (a.type === 'brownfield_assessment') {
        const existing = result.get(a.projectId);
        if (!existing || new Date(a.updatedAt) > new Date(existing.updatedAt)) {
          result.set(a.projectId, a);
        }
      }
    }
    return result;
  }, [artefacts]);

  // All unique project names for filter dropdown
  const projectOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const a of artefacts) {
      if (!seen.has(a.projectId)) seen.set(a.projectId, a.projectName ?? 'Unknown');
    }
    return [...seen.entries()].map(([id, name]) => ({ id, name }));
  }, [artefacts]);

  // ── Styles ──────────────────────────────────────────────────────────────

  const cardStyle: React.CSSProperties = {
    background: 'var(--surface)',
    borderRadius: 8,
    borderLeft: '4px solid var(--primary)',
    marginBottom: 16,
    overflow: 'hidden',
  };

  const cardHeaderStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '14px 20px', cursor: 'pointer', userSelect: 'none',
  };

  const phaseHeaderStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '8px 20px 8px 28px', cursor: 'pointer', userSelect: 'none',
    background: 'var(--bg)',
  };

  const artefactRowStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '8px 20px 8px 48px', cursor: 'pointer',
    transition: 'background 100ms',
  };

  const selectStyle: React.CSSProperties = {
    fontFamily: 'var(--font-body)', fontSize: 13,
    padding: '6px 12px', borderRadius: 6,
    border: '1px solid var(--border)', background: 'var(--surface)',
    color: 'var(--text)', outline: 'none',
  };

  const searchStyle: React.CSSProperties = {
    fontFamily: 'var(--font-body)', fontSize: 13,
    padding: '6px 12px', borderRadius: 6,
    border: '1px solid var(--border)', background: 'var(--surface)',
    color: 'var(--text)', outline: 'none', width: 220,
  };

  return (
    <div className="flex min-h-screen" style={{ background: 'var(--bg)' }}>
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--text)' }}>Artefacts</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <select
              value={filterProject}
              onChange={e => setFilterProject(e.target.value)}
              style={selectStyle}
            >
              <option value="all">All Projects</option>
              {projectOptions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <input
              type="text"
              placeholder="Search artefacts…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={searchStyle}
            />
            <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>
              {artefacts.length} total
            </span>
          </div>
        </div>

        {error && (
          <div className="mx-4 mt-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3 flex-shrink-0">
            {error}
          </div>
        )}

        {recoveryNotice && (
          <div ref={recoveryNoticeRef}>
            <Phase25RecoveryNotice notice={recoveryNotice} onDismiss={() => setRecoveryNotice(null)} />
          </div>
        )}

        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Left panel — project cards */}
          <div
            ref={leftPanelRef}
            className="flex-shrink-0 overflow-y-auto"
            style={{ width: panelWidth, padding: 16 }}
            data-testid="artefacts-left-panel"
          >
            {loading ? (
              <div style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }} className="py-12 text-center">Loading…</div>
            ) : projectGroups.length === 0 ? (
              <div style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }} className="py-12 text-center px-4">
                {search ? 'No artefacts match your search.' : 'No artefacts yet. Complete a project phase to generate one.'}
              </div>
            ) : (
              projectGroups.map(group => {
                const isProjectOpen = expandedProjects.has(group.projectId);
                const openPhases = expandedPhases[group.projectId] ?? new Set<string>();
                // On first open, auto-expand all phases that have artefacts
                const effectiveOpenPhases = isProjectOpen && !expandedPhases[group.projectId]
                  ? new Set(group.phases.map(p => p.phase))
                  : openPhases;

                return (
                  <div key={group.projectId} style={cardStyle}>
                    {/* Project header */}
                    <div
                      style={cardHeaderStyle}
                      onClick={() => toggleProject(group.projectId)}
                    >
                      <span style={{
                        fontSize: 11, color: 'var(--text-secondary)',
                        display: 'inline-block',
                        transform: isProjectOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                        transition: 'transform 150ms',
                      }}>▶</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span
                            style={{
                              fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700,
                              color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}
                            title={group.name}
                          >
                            {group.name}
                          </span>
                          <span style={{
                            fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600,
                            padding: '2px 8px', borderRadius: 9999,
                            background: 'rgba(124,58,237,0.10)', color: 'var(--primary)',
                            whiteSpace: 'nowrap',
                          }}>
                            {phaseDisplayName(group.phase)}
                          </span>
                        </div>
                        <span style={{
                          fontFamily: 'var(--font-body)', fontSize: 11,
                          color: 'var(--text-secondary)',
                        }}>
                          Last activity: {relativeTime(group.lastActivity)}
                        </span>
                      </div>
                      <span style={{
                        fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600,
                        color: 'var(--text-secondary)',
                        background: 'var(--bg)', padding: '2px 8px', borderRadius: 6,
                      }}>
                        {group.count}
                      </span>
                    </div>

                    {/* Analyse imported artefacts + Generate Assessment buttons */}
                    {isProjectOpen && (
                      <div style={{ padding: '8px 20px 4px 28px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {(projectsWithImported.get(group.projectId) ?? 0) > 0 && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleAnalyse(group.projectId); }}
                            disabled={analysing}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 6,
                              padding: '6px 14px', borderRadius: 6,
                              border: '1px solid var(--primary)', background: 'rgba(124,58,237,0.08)',
                              color: 'var(--primary)', cursor: analysing ? 'wait' : 'pointer',
                              fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600,
                              opacity: analysing && analysingProjectId === group.projectId ? 0.6 : 1,
                            }}
                          >
                            {analysing && analysingProjectId === group.projectId
                              ? 'Analysing\u2026 (this may take a minute)'
                              : `Analyse ${projectsWithImported.get(group.projectId)} imported artefact${projectsWithImported.get(group.projectId)! > 1 ? 's' : ''}`}
                          </button>
                        )}
                        {group.count > 0 && (() => {
                          const hasImported = (projectsWithImported.get(group.projectId) ?? 0) > 0;
                          const existingBAR = projectsWithBAR.get(group.projectId);
                          const isGenerating = generatingBAR && barProjectId === group.projectId;
                          const canGenerate = hasImported && !isGenerating;

                          return (
                            <>
                              {existingBAR && !isGenerating && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); openTab(existingBAR); }}
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    padding: '6px 14px', borderRadius: 6,
                                    border: '1px solid var(--primary)', background: 'rgba(124,58,237,0.08)',
                                    color: 'var(--primary)', cursor: 'pointer',
                                    fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600,
                                  }}
                                >
                                  View Assessment Report
                                </button>
                              )}
                              <button
                                onClick={(e) => { e.stopPropagation(); if (canGenerate) handleGenerateBAR(group.projectId); }}
                                disabled={!canGenerate}
                                title={!hasImported ? 'Import existing artefacts first to generate an assessment report' : undefined}
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 6,
                                  padding: '6px 14px', borderRadius: 6,
                                  border: `1px solid ${hasImported ? 'var(--primary)' : 'var(--border)'}`,
                                  background: hasImported ? 'rgba(124,58,237,0.08)' : 'var(--bg)',
                                  color: hasImported ? 'var(--primary)' : 'var(--text-secondary)',
                                  cursor: isGenerating ? 'wait' : !hasImported ? 'not-allowed' : 'pointer',
                                  fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600,
                                  opacity: !hasImported || isGenerating ? 0.6 : 1,
                                }}
                              >
                                {isGenerating
                                  ? '⟳ Generating\u2026'
                                  : existingBAR
                                    ? 'Regenerate Assessment'
                                    : 'Generate Brownfield Assessment'}
                              </button>
                            </>
                          );
                        })()}
                        {/* Recover Phase 2.5 artefacts — backfills any
                            screen_inventory / screen_spec /
                            component_catalogue rows that the live agent
                            loop missed. Idempotent: re-running on a
                            project that already has the rows reports
                            them as alreadyPresent. Most useful for old
                            projects whose UX session completed before
                            the post-stage-7 retro-save shipped. */}
                        {(() => {
                          const isRecovering = recoveringProjectId === group.projectId;
                          const anyRecovering = recoveringProjectId !== null;
                          return (
                            <button
                              onClick={(e) => { e.stopPropagation(); if (!anyRecovering) handleRecoverPhase2_5(group.projectId); }}
                              disabled={anyRecovering}
                              title="Re-scan UX-agent chat sessions for any Phase 2.5 artefacts that didn't persist during the live agent loop."
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 6,
                                padding: '6px 14px', borderRadius: 6,
                                border: '1px solid var(--primary)', background: 'rgba(124,58,237,0.08)',
                                color: 'var(--primary)',
                                cursor: isRecovering ? 'wait' : anyRecovering ? 'not-allowed' : 'pointer',
                                fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600,
                                opacity: anyRecovering && !isRecovering ? 0.6 : 1,
                              }}
                            >
                              {isRecovering ? '⟳ Recovering…' : 'Recover UX artefacts'}
                            </button>
                          );
                        })()}
                      </div>
                    )}

                    {/* Phase accordions */}
                    {isProjectOpen && group.phases.map(phaseGroup => {
                      const isPhaseOpen = effectiveOpenPhases.has(phaseGroup.phase);
                      const isEmpty = phaseGroup.items.length === 0;

                      return (
                        <div key={phaseGroup.phase}>
                          {/* Phase header */}
                          <div
                            style={{
                              ...phaseHeaderStyle,
                              opacity: isEmpty ? 0.5 : 1,
                            }}
                            onClick={() => togglePhase(group.projectId, phaseGroup.phase)}
                          >
                            <span style={{
                              fontSize: 9, color: 'var(--text-secondary)',
                              display: 'inline-block',
                              transform: isPhaseOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                              transition: 'transform 150ms',
                            }}>▶</span>
                            <span style={{
                              fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500,
                              color: 'var(--text)', flex: 1,
                            }}>
                              {phaseGroup.phase}
                            </span>
                            <span style={{
                              fontFamily: 'var(--font-mono)', fontSize: 11,
                              color: 'var(--text-secondary)',
                            }}>
                              ({phaseGroup.items.length})
                            </span>
                          </div>

                          {/* Artefact rows */}
                          {isPhaseOpen && phaseGroup.items.map(a => {
                            const isActive = activeTabId === a.id;
                            const sc = STATUS_COLORS[a.status] ?? { bg: 'var(--surface-alt)', color: 'var(--text-secondary)' };
                            // Imported rows keep the user's filename; everything else
                            // resolves to a canonical type label so truncated noise like
                            // "Phoenix Ad…" can't happen on short viewports. `importedFrom`
                            // is stable across version edits — version `source` can flip
                            // to 'authored' after a manual update, which would incorrectly
                            // strip the filename off rows that were originally uploads.
                            const isImported = !!a.importedFrom;
                            const displayName = getArtefactDisplayName(
                              isImported ? 'imported' : a.type,
                              a.title,
                            );
                            const displayTitle = displayName.length > 50
                              ? displayName.slice(0, 47) + '…'
                              : displayName;

                            return (
                              <div
                                key={a.id}
                                style={{
                                  ...artefactRowStyle,
                                  background: isActive ? 'rgba(124,58,237,0.08)' : 'transparent',
                                  borderLeft: isActive ? '3px solid var(--primary)' : '3px solid transparent',
                                }}
                                title={displayName}
                                onClick={() => openTab(a)}
                                onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLDivElement).style.background = 'var(--surface-alt)'; }}
                                onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                              >
                                <span style={{ fontSize: 14, flexShrink: 0 }}>
                                  {TYPE_ICONS[a.type] ?? '📄'}
                                </span>
                                <span style={{
                                  fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500,
                                  color: 'var(--text)', flex: 1,
                                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}>
                                  {displayTitle}
                                </span>
                                <span style={{
                                  fontFamily: 'var(--font-mono)', fontSize: 10,
                                  color: 'var(--text-secondary)', flexShrink: 0,
                                }}>
                                  v{a.currentVersion}
                                </span>
                                <span style={{
                                  fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 500,
                                  padding: '1px 6px', borderRadius: 9999,
                                  background: sc.bg, color: sc.color, flexShrink: 0,
                                }}>
                                  {STATUS_LABELS[a.status] ?? a.status}
                                </span>
                                {(evidenceCounts.get(a.id) ?? 0) > 0 && (
                                  <span style={{
                                    fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 500,
                                    padding: '1px 6px', borderRadius: 9999,
                                    background: 'rgba(13,148,136,0.10)', color: '#0d9488', flexShrink: 0,
                                  }}>
                                    {evidenceCounts.get(a.id)} link{evidenceCounts.get(a.id)! > 1 ? 's' : ''}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                );
              })
            )}
          </div>

          {/* Drag handle — resize left panel; double-click resets to default;
              arrow keys nudge for keyboard users. */}
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize artefacts panel"
            aria-valuemin={panelMin}
            aria-valuemax={panelMax}
            aria-valuenow={panelWidth}
            tabIndex={0}
            data-testid="artefacts-panel-resize-handle"
            onMouseDown={panelHandleProps.onMouseDown}
            onDoubleClick={panelHandleProps.onDoubleClick}
            onKeyDown={panelHandleProps.onKeyDown}
            style={{
              flex: '0 0 auto',
              width: 5,
              cursor: 'col-resize',
              background: panelDragging ? 'rgba(124, 58, 237, 0.6)' : 'transparent',
              borderLeft: '1px solid var(--border)',
              transition: panelDragging ? 'none' : 'background 120ms ease, box-shadow 120ms ease',
              userSelect: 'none',
              outline: 'none',
            }}
            onMouseEnter={e => {
              if (!panelDragging) (e.currentTarget as HTMLDivElement).style.background = 'rgba(124, 58, 237, 0.3)';
            }}
            onMouseLeave={e => {
              if (!panelDragging) (e.currentTarget as HTMLDivElement).style.background = 'transparent';
            }}
            onFocus={e => {
              const el = e.currentTarget as HTMLDivElement;
              el.style.background = 'rgba(124, 58, 237, 0.6)';
              // 2px focus ring keyboard users can actually see — outline:none
              // strips the browser default for the 5px-wide separator.
              el.style.boxShadow = '0 0 0 2px var(--primary)';
            }}
            onBlur={e => {
              const el = e.currentTarget as HTMLDivElement;
              if (!panelDragging) el.style.background = 'transparent';
              el.style.boxShadow = 'none';
            }}
          />

          {/* Right panel — tabbed viewer */}
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <ArtefactTabs
              openTabs={openTabs}
              activeTabId={activeTabId}
              onSwitchTab={setActiveTabId}
              onCloseTab={closeTab}
              onMetadataLoaded={handleMetadataLoaded}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
